export type KeyboardShortcutPlatformVariant = 'apple' | 'nonApple';

const HTML_ENTITY_MAP: Record<string, string> = {
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
};

const SHORTCUT_SEPARATOR_REGEX = /(\s*(?:->|→|\+|-)\s*)/;

const SYMBOL_LABELS = {
    apple: {
        command: '⌘',
        option: '⌥',
        ctrl: '⌃',
        shift: '⇧',
        return: '⏎',
        tab: '⇥',
    },
    nonApple: {
        command: 'Ctrl',
        option: 'Alt',
        ctrl: '⌃',
        shift: '⇧',
        return: '⏎',
        tab: '⇥',
    },
} as const;

const TEXT_LABELS = {
    apple: {
        command: '⌘',
        option: 'Option',
        ctrl: 'Ctrl',
        shift: 'Shift',
        return: 'Return',
        tab: 'Tab',
    },
    nonApple: {
        command: 'Ctrl',
        option: 'Alt',
        ctrl: 'Ctrl',
        shift: 'Shift',
        return: 'Enter',
        tab: 'Tab',
    },
} as const;

const getPlatformLabels = (platformVariant: KeyboardShortcutPlatformVariant, preferSymbol: boolean) => {
    const platformKey = platformVariant === 'apple' ? 'apple' : 'nonApple';

    return preferSymbol ? SYMBOL_LABELS[platformKey] : TEXT_LABELS[platformKey];
};

export const normalizeKeyboardShortcutToken = (token: string): string => {
    const normalizedToken = token.trim();

    return HTML_ENTITY_MAP[normalizedToken] ?? normalizedToken;
};

const normalizeKeyboardShortcutSeparator = (separator: string): string => {
    const trimmedSeparator = separator.trim();

    if (trimmedSeparator === '->') return '→';
    if (trimmedSeparator === '-') return '';

    return trimmedSeparator;
};

const getKeyboardShortcutDisplayValueForSingleToken = ({
    token,
    preferSymbol = false,
    platformVariant = 'apple',
}: {
    token: string;
    preferSymbol?: boolean;
    platformVariant?: KeyboardShortcutPlatformVariant;
}): string => {
    const normalizedToken = normalizeKeyboardShortcutToken(token);
    const tokenKey = normalizedToken.toLowerCase();
    const labels = getPlatformLabels(platformVariant, preferSymbol);

    switch (tokenKey) {
        case '⌘':
        case 'cmd':
        case 'command':
            return labels.command;
        case '⌥':
        case 'alt':
        case 'opt':
        case 'option':
            return labels.option;
        case 'ctrl':
        case 'control':
            return labels.ctrl;
        case 'shift':
            return labels.shift;
        case 'return':
        case 'enter':
            return labels.return;
        case 'tab':
            return labels.tab;
        case 'escape':
        case 'esc':
            return 'Esc';
        case 'delete':
            return 'Delete';
        default:
            return normalizedToken;
    }
};

export const getKeyboardShortcutDisplayValue = ({
    token,
    preferSymbol = false,
    platformVariant = 'apple',
}: {
    token: string;
    preferSymbol?: boolean;
    platformVariant?: KeyboardShortcutPlatformVariant;
}): string => {
    const normalizedToken = normalizeKeyboardShortcutToken(token);

    if (!SHORTCUT_SEPARATOR_REGEX.test(normalizedToken)) {
        return getKeyboardShortcutDisplayValueForSingleToken({
            token: normalizedToken,
            preferSymbol,
            platformVariant,
        });
    }

    return normalizedToken
        .split(SHORTCUT_SEPARATOR_REGEX)
        .filter(Boolean)
        .map((part) => {
            if (SHORTCUT_SEPARATOR_REGEX.test(part)) {
                return normalizeKeyboardShortcutSeparator(part);
            }

            return getKeyboardShortcutDisplayValueForSingleToken({
                token: part,
                preferSymbol,
                platformVariant,
            });
        })
        .join('');
};
